import { postWorkflowWebhook, readWorkflowResponse } from '@/lib/workflow-webhook';
import { ServiceDeskRuleError } from './service-desk.types';

/**
 * Published webhook URLs of the service desk workflows (blocks/workflows/service-desk). Every write to
 * the five application schemas goes through one of them: they save with the service credential and
 * check the caller's permission, ownership, department and the application's status themselves, so
 * nothing the browser sends can approve its own application or change someone else's.
 */
const WEBHOOK_URLS = {
  NOC_APPLICATION_SUBMIT: import.meta.env.VITE_NOC_APPLICATION_SUBMIT_WEBHOOK_URL,
  NOC_APPLICATION_WITHDRAW: import.meta.env.VITE_NOC_APPLICATION_WITHDRAW_WEBHOOK_URL,
  NOC_APPLICATION_REVIEW: import.meta.env.VITE_NOC_APPLICATION_REVIEW_WEBHOOK_URL,
  NOC_APPLICATION_GENERATE: import.meta.env.VITE_NOC_APPLICATION_GENERATE_WEBHOOK_URL,
  NOC_APPLICATION_DELETE: import.meta.env.VITE_NOC_APPLICATION_DELETE_WEBHOOK_URL,
  BYOD_APPLICATION_SUBMIT: import.meta.env.VITE_BYOD_APPLICATION_SUBMIT_WEBHOOK_URL,
  BYOD_APPLICATION_WITHDRAW: import.meta.env.VITE_BYOD_APPLICATION_WITHDRAW_WEBHOOK_URL,
  BYOD_APPLICATION_REVIEW: import.meta.env.VITE_BYOD_APPLICATION_REVIEW_WEBHOOK_URL,
  BUSINESS_CARD_APPLICATION_SUBMIT: import.meta.env
    .VITE_BUSINESS_CARD_APPLICATION_SUBMIT_WEBHOOK_URL,
  BUSINESS_CARD_APPLICATION_WITHDRAW: import.meta.env
    .VITE_BUSINESS_CARD_APPLICATION_WITHDRAW_WEBHOOK_URL,
  BUSINESS_CARD_APPLICATION_STATUS: import.meta.env
    .VITE_BUSINESS_CARD_APPLICATION_STATUS_WEBHOOK_URL,
  ID_CARD_APPLICATION_SUBMIT: import.meta.env.VITE_ID_CARD_APPLICATION_SUBMIT_WEBHOOK_URL,
  ID_CARD_APPLICATION_WITHDRAW: import.meta.env.VITE_ID_CARD_APPLICATION_WITHDRAW_WEBHOOK_URL,
  ID_CARD_APPLICATION_STATUS: import.meta.env.VITE_ID_CARD_APPLICATION_STATUS_WEBHOOK_URL,
  METLIFE_APPLICATION_SUBMIT: import.meta.env.VITE_METLIFE_APPLICATION_SUBMIT_WEBHOOK_URL,
  METLIFE_APPLICATION_WITHDRAW: import.meta.env.VITE_METLIFE_APPLICATION_WITHDRAW_WEBHOOK_URL,
  METLIFE_APPLICATION_STATUS: import.meta.env.VITE_METLIFE_APPLICATION_STATUS_WEBHOOK_URL,
  METLIFE_APPLICATION_CANCEL: import.meta.env.VITE_METLIFE_APPLICATION_CANCEL_WEBHOOK_URL,
} as const;

export type ServiceDeskWorkflow = keyof typeof WEBHOOK_URLS;

interface RefusalData {
  /** The translation key of the refusal (service-desk / generic localization module). */
  MessageKey?: string;
}

/**
 * Runs a service desk workflow. A refusal throws a `ServiceDeskRuleError` with its translation key (or
 * the workflow's English message, for invalid input), which the global error toast shows; on success
 * the id of the saved application is returned.
 */
export const runServiceDeskWorkflow = async (
  workflow: ServiceDeskWorkflow,
  body: Record<string, unknown>
): Promise<string> => {
  const response = await postWorkflowWebhook(
    WEBHOOK_URLS[workflow] ?? '',
    `VITE_${workflow}_WEBHOOK_URL`,
    body
  );
  const result = readWorkflowResponse<RefusalData>(response, 'SERVICE_DESK_SAVE_FAILED');
  if (!result.IsSuccessful) {
    throw new ServiceDeskRuleError(result.Data?.MessageKey || result.Message);
  }
  return result.EntityId;
};
